import { useState, createContext, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import i18n from '../i18n';
import {
  createDebit,
  listDebits,
  updateDebit,
  deleteDebit,
  togglePayDebit,
  listPendingDebitApprovals,
  approveDebitParticipation,
  rejectDebitParticipation
} from '../api/debitos';

const isPt = () => (i18n.language || 'pt').startsWith('pt');

export const DebitosContext = createContext({});

function DebitosProvider({ children }) {
  const [debitos, setDebitos] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);

  const saveDebitos = useCallback(async (data, mostrarMensagem = true) => {
    try {
      const created = await createDebit(data);
      setDebitos(prev => [...prev, created]);
      if (mostrarMensagem) {
        toast.success(isPt() ? 'Débito cadastrado' : 'Expense saved');
      }
      return created;
    } catch (error) {
      toast.error(error?.response?.data?.error || (isPt() ? 'Algo deu errado' : 'Something went wrong'));
      return null;
    }
  }, []);

  const updateDebitsValues = useCallback(async (data, options = {}) => {
    try {
      const updated = await updateDebit(data._id, data, options);
      setDebitos(prev =>
        prev.map(d => (d._id === updated._id ? updated : d))
      );
      toast.success(isPt() ? 'Alterado com sucesso' : 'Updated');
      return updated;
    } catch (error) {
      toast.error(error?.response?.data?.error || (isPt() ? 'Erro ao alterar' : 'Failed to update'));
      return null;
    }
  }, []);

  const excluirDebits = useCallback(async (id, mensagem = true, options = {}) => {
    try {
      await deleteDebit(id, options);
      setDebitos((prev) => {
        const target = prev.find((d) => d._id === id);
        if (options?.scope === 'all' && target?.recurrenceId) {
          // Parcelado: remove toda a recorrência do estado local
          return prev.filter((d) => String(d.recurrenceId || '') !== String(target.recurrenceId));
        }
        if (options?.scope === 'all' && target?.contaFixa === 0) {
          // Fixo cascade: remove o item e os de meses futuros com mesma descrição/categoria
          return prev.filter((d) => {
            if (d._id === id) return false;
            if (d.contaFixa === 0
                && d.descricao === target.descricao
                && d.categoria === target.categoria
                && (d.dataReferencia || '') > (target.dataReferencia || '')) {
              return false;
            }
            return true;
          });
        }
        return prev.filter((d) => d._id !== id);
      });
      if (mensagem) {
        toast.success(isPt() ? 'Dado excluído' : 'Deleted');
      }
      return true;
    } catch (error) {
      toast.error(error?.response?.data?.error || (isPt() ? 'Erro ao excluir' : 'Failed to delete'));
      return false;
    }
  }, []);

  const getDebitos = useCallback(async (usuario, filtros = {}) => {
    try {
      const list = await listDebits(usuario, filtros);
      setDebitos(list);
      return list;
    } catch {
      return [];
    }
  }, []);

  const getPendingApprovals = useCallback(async (filtros = {}) => {
    try {
      const list = await listPendingDebitApprovals(filtros);
      setPendingApprovals(list);
      return list;
    } catch {
      return [];
    }
  }, []);

  const payDebito = useCallback(async (id, options = {}) => {
    try {
      const toggled = await togglePayDebit(id, options);
      setDebitos(prev =>
        prev.map(d => (d._id === toggled._id ? toggled : d))
      );
      return true;
    } catch {
      return false;
    }
  }, []);

  const approveParticipation = useCallback(async (debitId) => {
    try {
      const updated = await approveDebitParticipation(debitId);
      setPendingApprovals((prev) => prev.filter((item) => item._id !== debitId));
      setDebitos((prev) => {
        if (prev.some((item) => item._id === updated._id)) {
          return prev.map((item) => (item._id === updated._id ? updated : item));
        }

        return [updated, ...prev];
      });
      return true;
    } catch (error) {
      toast.error(error?.response?.data?.error || (isPt() ? 'Erro ao aprovar participação' : 'Failed to approve participation'));
      return false;
    }
  }, []);

  const rejectParticipation = useCallback(async (debitId) => {
    try {
      await rejectDebitParticipation(debitId);
      setPendingApprovals((prev) => prev.filter((item) => item._id !== debitId));
      return true;
    } catch (error) {
      toast.error(error?.response?.data?.error || (isPt() ? 'Erro ao recusar participação' : 'Failed to reject participation'));
      return false;
    }
  }, []);

  const value = useMemo(() => ({
    debitos,
    pendingApprovals,
    saveDebitos,
    getDebitos,
    getPendingApprovals,
    updateDebitsValues,
    excluirDebits,
    payDebito,
    approveParticipation,
    rejectParticipation
  }), [
    debitos,
    pendingApprovals,
    saveDebitos,
    getDebitos,
    getPendingApprovals,
    updateDebitsValues,
    excluirDebits,
    payDebito,
    approveParticipation,
    rejectParticipation
  ]);

  return (
    <DebitosContext.Provider
      value={value}
    >
      {children}
    </DebitosContext.Provider>
  );
}

export default DebitosProvider;
